import { createTheme } from '@mui/material/styles';

export const colors = {
  // Primary — brand pink
  primary: '#f48fb4',
  primaryLight: '#f7a5c6',
  primaryDark: '#e96d9a',
  primaryTint: '#f8f9fa',
  primaryText: '#333333',

  // Secondary — deeper pink
  secondary: '#e96d9a',
  secondaryLight: '#f4a7b9',
  secondaryDark: '#d45180',

  // Sidebar — black
  sidebarBg: '#000000',

  // Background — white
  background: '#ffffff',
  backgroundSecondary: '#ffffff',
  backgroundTertiary: '#f8f9fa',

  // Text — dark on light background
  textPrimary: '#333333',
  textSecondary: '#666666',
  greyText: '#666666',
  greyText2: '#999999',
  greyText3: '#CCCCCC',
  greyText4: '#333333',
  greyText5: '#666666',
  greyLight2: '#666666',
  greyLight3: '#999999',
  greyLight4: '#CCCCCC',
  greyLight5: '#333333',
  greyLight6: '#000000',
  disabledBorder: '#CCCCCC',

  // Status
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196f3',

  // System
  white: '#ffffff',
  black: '#000000',
  divider: '#e0e0e0',
};

export const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary,
      light: colors.secondaryLight,
      dark: colors.secondaryDark,
      contrastText: '#ffffff',
    },
    background: {
      default: colors.background,
      paper: colors.backgroundSecondary,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    error: { main: colors.error },
    success: { main: colors.success },
    warning: { main: colors.warning },
    info: { main: colors.info },
    divider: colors.divider,
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Kumbh Sans", "Inter", "Roboto", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700, color: colors.textPrimary },
    h2: { fontSize: '2rem', fontWeight: 700, color: colors.textPrimary },
    h3: { fontSize: '1.75rem', fontWeight: 600, color: colors.textPrimary },
    h4: { fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary },
    h5: { fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary },
    h6: { fontSize: '1rem', fontWeight: 600, color: colors.textPrimary },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 18px',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(244, 143, 180, 0.35)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(244, 143, 180, 0.5)',
          },
        },
        outlined: {
          borderColor: colors.primary,
          '&:hover': {
            borderColor: colors.primaryDark,
            backgroundColor: `${colors.primary}15`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: '0 2px 12px rgba(244, 143, 180, 0.1)',
          border: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 14,
        },
        elevation2: {
          boxShadow: '0 2px 12px rgba(244, 143, 180, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: colors.primary,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          boxShadow: '0 8px 40px rgba(244, 143, 180, 0.2)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: colors.divider },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f8f9fa',
          fontWeight: 600,
          color: colors.textPrimary,
        },
      },
    },
  },
});

export default theme;
